import { memo, useRef, useState, type FC, type ReactNode } from "react";
import { AutoComplete, type RefSelectProps } from "antd";
import { Check, ChevronDown, Pencil, RefreshCw, Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";
import MiniSpinner from "src/components/reusable/Spinner/MiniSpinner";
import StyledTextInput from "src/components/reusable/StyledTextInput";
import type { GeneratedSubjectLine } from "src/services/EngagementAPIs";
import { formatNumber } from "src/utils/numberFormat";
import CampaignFormField from "../../../../shared/CampaignFormField";
import { useRequiredFieldError } from "../../../../shared/UseRequiredFieldError";
import { MAX_SUBJECT_LENGTH } from "../AiCampaignConstants";

const KEY = "campaignFlow.aiWizard.senderRecipients";

export interface SubjectSuggestionsState {
  items: GeneratedSubjectLine[];
  bestSubjectLine?: string;
  isLoading: boolean;
  refresh: () => void;
}

interface SubjectLineFieldProps {
  id: string;
  value: string;
  onChange: (_value: string) => void;
  suggestions: SubjectSuggestionsState;
}

const keepInputFocus = (event: { preventDefault: () => void }) => event.preventDefault();

const SubjectLineField: FC<SubjectLineFieldProps> = ({ id, value, onChange, suggestions }) => {
  const { t, i18n } = useTranslation();
  const inputRef = useRef<RefSelectProps>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isOpenManually, setIsOpenManually] = useState(false);
  const { hasError, handleBlur } = useRequiredFieldError(value, true);

  const options = suggestions.items.map(suggestion => ({
    value: suggestion.subject_line,
    label: (
      <span className="subject-line__option">
        <span className="subject-line__option-text">
          {value === suggestion.subject_line && <Check size={14} aria-hidden />}
          {suggestion.subject_line}
          {suggestion.subject_line === suggestions.bestSubjectLine && (
            <span className="subject-line__best-tag">{t(`${KEY}.bestPick`)}</span>
          )}
        </span>
        <span className="subject-line__option-tag">{suggestion.tone}</span>
      </span>
    ),
  }));

  const writeYourOwn = () => {
    setIsOpenManually(true);
    setIsOpen(false);
    inputRef.current?.focus();
  };

  const openSuggestions = () => {
    setIsOpenManually(false);
    setIsOpen(true);
    inputRef.current?.focus();
  };

  const handleDropdownVisibleChange = (open: boolean) => {
    if (!open || !isOpenManually) {
      setIsOpen(open);
    }
  };

  const renderDropdown = (menu: ReactNode) => (
    <div className="subject-line__dropdown">
      <div className="subject-line__dropdown-header">
        <span>{t(`${KEY}.suggestionsHeading`)}</span>
        <button
          type="button"
          className="subject-line__link"
          onMouseDown={keepInputFocus}
          onClick={suggestions.refresh}
          disabled={suggestions.isLoading}
        >
          <RefreshCw
            size={12}
            aria-hidden
            className={suggestions.isLoading ? "subject-line__spin" : undefined}
          />
          {t(`${KEY}.generateMore`)}
        </button>
      </div>
      {suggestions.isLoading ? (
        <div className="subject-line__loading">
          <MiniSpinner />
          <span>{t(`${KEY}.generatingSuggestions`)}</span>
        </div>
      ) : (
        menu
      )}
      <button
        type="button"
        className="subject-line__write-own"
        onMouseDown={keepInputFocus}
        onClick={writeYourOwn}
      >
        <Pencil size={12} aria-hidden />
        {t(`${KEY}.writeYourOwn`)}
      </button>
    </div>
  );

  return (
    <CampaignFormField
      label={t("campaignFlow.aiWizard.details.subjectLine")}
      htmlFor={id}
      required
      hasError={hasError}
    >
      <AutoComplete
        ref={inputRef}
        value={value}
        options={options}
        open={isOpen}
        onDropdownVisibleChange={handleDropdownVisibleChange}
        onChange={onChange}
        filterOption={false}
        defaultActiveFirstOption={false}
        dropdownRender={renderDropdown}
        notFoundContent={
          <span className="subject-line__empty">
            {suggestions.isLoading ? t(`${KEY}.generatingSuggestions`) : t(`${KEY}.noSuggestions`)}
          </span>
        }
        popupClassName="subject-line__popup"
        className="subject-line__select"
      >
        <StyledTextInput
          id={id}
          maxLength={MAX_SUBJECT_LENGTH}
          onBlur={handleBlur}
          onClick={openSuggestions}
          autoComplete="off"
          placeholder={t(`${KEY}.subjectPlaceholder`)}
          className="campaign-flow__input"
          suffix={
            <button
              type="button"
              className={`subject-line__arrow${isOpen ? " subject-line__arrow--open" : ""}`}
              aria-label={t(`${KEY}.suggestionsHeading`)}
              onMouseDown={keepInputFocus}
              onClick={() => (isOpen ? setIsOpen(false) : openSuggestions())}
            >
              <ChevronDown size={16} aria-hidden />
            </button>
          }
        />
      </AutoComplete>
      <div className="subject-line__meta">
        <button type="button" className="subject-line__link" onClick={openSuggestions}>
          <Sparkles size={12} aria-hidden />
          {t(`${KEY}.suggestWithAi`)}
        </button>
        <span className="campaign-flow-field__counter">
          {formatNumber(value.length, {}, i18n.language)}/
          {formatNumber(MAX_SUBJECT_LENGTH, {}, i18n.language)}
        </span>
      </div>
    </CampaignFormField>
  );
};

export default memo(SubjectLineField);
